"use client";

import { ArrowRight, Sprout, Droplets, Leaf, Scissors, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ElProcesoDeTransformaciN() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const steps = [{
    number: "01",
    icon: Sprout,
    title: "Consulta Inicial",
    description: "Evaluación personalizada de su salud capilar y expectativas reales.",
    duration: "00:00–01:00 HRS"
  }, {
    number: "02",
    icon: Droplets,
    title: "Planificación",
    description: "Diseño de la línea frontal y cálculo de unidades foliculares.",
    duration: "01:00–02:00 HRS"
  }, {
    number: "03",
    icon: Leaf,
    title: "Intervención",
    description: "Procedimiento de precisión con técnica avanzada de extracción.",
    duration: "02:00–05:30 HRS"
  }, {
    number: "04",
    icon: Scissors,
    title: "Seguimiento",
    description: "Protocolo post-operatorio para asegurar la máxima supervivencia.",
    duration: "05:30–06:00 HRS"
  }, {
    number: "05",
    icon: Truck,
    title: "Resultados",
    description: "Evolución natural hacia una densidad capilar recuperada.",
    duration: "06:00+ MESES"
  }];
  return <section data-section-id="5264" id="el-proceso-de-transformación" className="bg-background text-foreground relative overflow-hidden ![padding:100px_20px_40px_32px]">
      <div className="absolute top-12 left-0 right-0 flex items-center pointer-events-none opacity-30">
        <div className="flex-1 flex items-center justify-between">
          {Array.from({
          length: 40
        }).map((_, i) => <div key={i} data-index={i} className="w-px bg-border" style={{
          height: i % 5 === 0 ? "12px" : "6px"
        }} />)}
        </div>
      </div>
      <div className="absolute bottom-12 left-0 right-0 flex items-center pointer-events-none opacity-30">
        <div className="flex-1 flex items-center justify-between">
          {Array.from({
          length: 40
        }).map((_, i) => <div key={i} data-index={i} className="w-px bg-border" style={{
          height: i % 5 === 0 ? "12px" : "6px"
        }} />)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs tracking-[0.2em] text-primary mb-6 uppercase !font-bold md:text-sm"> EL PROCESO DE TRANSFORMACIÓN </p>
          <h2 className="font-default font-medium text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground mb-6">
            Resultados naturales, tratados con <em className="italic text-primary">precisión clínica</em>
          </h2>
          <p className="font-default text-sm md:text-base text-foreground/70 tracking-wide">Un camino diseñado para su bienestar capilar</p>
        </div>

        <div ref={lineRef} className="hidden lg:block relative">
          <div className="absolute top-[88px] left-[10%] right-[10%] h-px bg-border" />
          <div className="absolute top-[88px] left-[10%] h-px bg-primary transition-all duration-[2000ms] ease-out" style={{
          width: lineVisible ? "80%" : "0%"
        }} />

          <div className="grid grid-cols-5 gap-6 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className={`flex flex-col items-center text-center transition-all duration-700 ease-out`} style={{
              transitionDelay: `${i * 200}ms`,
              opacity: lineVisible ? 1 : 0,
              transform: lineVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                  <div className="font-default text-sm tracking-[0.2em] text-primary mb-6 !font-bold">
                    {step.number}
                  </div>

                  <div className="relative mb-8">
                    <div className={`w-5 h-5 rounded-lg border-2 border-primary transition-all duration-500 ${lineVisible ? "bg-primary" : "bg-background"}`} style={{
                  transitionDelay: `${i * 200 + 400}ms`
                }} />
                  </div>

                  <Icon className="w-8 h-8 text-foreground mb-6" strokeWidth={1} />

                  <h3 className="font-default font-medium text-2xl xl:text-[28px] leading-tight text-foreground mb-4">
                    {step.title}
                  </h3>

                  <p className="text-sm text-foreground/70 leading-relaxed max-w-[200px] ![margin:0px_0px_5px_0px] !font-semibold">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        <div className="lg:hidden relative px-4 py-6">
          <div className="absolute top-2 bottom-2 left-[19px] w-px bg-border" />
          <div className="absolute top-2 left-[19px] w-px bg-primary transition-all duration-[2000ms] ease-out" style={{
          height: lineVisible ? "calc(100% - 16px)" : "0%"
        }} />

          <div className="space-y-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative pl-14 transition-all duration-700 ease-out" style={{
              transitionDelay: `${i * 150}ms`,
              opacity: lineVisible ? 1 : 0,
              transform: lineVisible ? "translateX(0)" : "translateX(-20px)"
            }}>
                  <div className={`absolute left-[11px] top-2 w-5 h-5 rounded-lg border-2 border-primary transition-all duration-500 ${lineVisible ? "bg-primary" : "bg-background"}`} style={{
                transitionDelay: `${i * 150 + 400}ms`
              }} />

                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-default text-sm tracking-[0.2em] text-primary">
                      {step.number}
                    </span>
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-foreground" strokeWidth={1} />
                  </div>

                  <h3 className="font-default font-medium text-2xl md:text-3xl leading-tight text-foreground mb-3 break-words">
                    {step.title}
                  </h3>

                  <p className="text-sm md:text-base text-foreground/70 leading-relaxed mb-4 max-w-md break-words">
                    {step.description}
                  </p>

                  <div className="font-default text-[10px] tracking-[0.15em] text-foreground/80 border border-border px-3 py-1.5 rounded-lg inline-block">
                    {step.duration}
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div ref={ctaRef} className={`mt-20 md:mt-28 text-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive underline-offset-4 h-9 px-4 py-2 has-[>svg]:px-3 text-foreground font-default text-sm tracking-[0.15em] uppercase no-underline group !font-bold disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:text-primary hover:no-underline w-full md:w-auto mt-6">
            <Link href="https://​https://msinstitutocapilar.com/contacto/" newTab>
              Solicitar consulta gratuita
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}